<script setup>
import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useCrmStore } from 'dashboard/stores/crm';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  opportunity: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['click']);

const crmStore = useCrmStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const statusBorderClass = {
  won: 'border-l-woot-500',
  lost: 'border-l-n-ruby-9',
  open: 'border-l-n-iris-9',
};

const borderClass =
  statusBorderClass[props.opportunity.status] || 'border-l-n-iris-9';

const originLabels = {
  manual: t('CRM.FORM.ORIGIN_MANUAL'),
  inbound_site: t('CRM.FORM.ORIGIN_INBOUND_SITE'),
  whatsapp: t('CRM.FORM.ORIGIN_WHATSAPP'),
  instagram: t('CRM.FORM.ORIGIN_INSTAGRAM'),
  email: t('CRM.FORM.ORIGIN_EMAIL'),
  referral: t('CRM.FORM.ORIGIN_REFERRAL'),
  other: t('CRM.FORM.ORIGIN_OTHER'),
};

const originLabel = originLabels[props.opportunity.origin] || null;

const isNextContactOverdue = computed(() => {
  if (!props.opportunity.next_contact_at || props.opportunity.status !== 'open')
    return false;
  return new Date(props.opportunity.next_contact_at) < new Date();
});

const handleClick = () => {
  crmStore.selectOpportunity(props.opportunity.id);
  emit('click');
};

const goToConversation = e => {
  e.stopPropagation();
  const { accountId } = route.params;
  const id = props.opportunity.conversation_id;
  if (!id || !accountId) return;
  router.push({
    name: 'inbox_conversation',
    params: { accountId, conversation_id: id },
  });
};
</script>

<template>
  <div
    class="bg-n-surface-1 rounded shadow-sm cursor-pointer hover:shadow-md transition-shadow duration-200 border-l-[3px] p-3 flex flex-col gap-2"
    :class="borderClass"
    @click="handleClick"
  >
    <p class="text-sm font-medium text-n-slate-12 truncate leading-tight">
      {{ opportunity.title }}
    </p>

    <p
      v-if="
        opportunity.value_cents !== null &&
        opportunity.value_cents !== undefined
      "
      class="text-xs font-semibold text-n-slate-11"
    >
      {{ opportunity.value_display }}
    </p>

    <div v-if="opportunity.tags?.length" class="flex flex-wrap gap-1">
      <span
        v-for="tag in opportunity.tags"
        :key="tag"
        class="text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-n-slate-4 text-n-slate-11"
      >
        {{ tag }}
      </span>
    </div>

    <div
      class="flex items-center justify-between gap-2 text-[11px] text-n-slate-10"
    >
      <span v-if="originLabel" class="truncate">{{ originLabel }}</span>
      <span v-if="opportunity.time_in_stage" class="truncate ml-auto">
        {{ opportunity.time_in_stage }}
      </span>
    </div>

    <p
      v-if="opportunity.next_contact_at"
      class="text-[11px] flex items-center gap-1"
      :class="
        isNextContactOverdue ? 'text-n-ruby-9 font-medium' : 'text-n-slate-10'
      "
    >
      {{ t('CRM.CARD.NEXT_CONTACT') }}:
      {{ new Date(opportunity.next_contact_at).toLocaleDateString() }}
    </p>

    <div class="flex items-center justify-between mt-1">
      <div v-if="opportunity.contact" class="flex items-center gap-1.5">
        <Avatar
          :src="opportunity.contact.avatar_url"
          :name="opportunity.contact.name"
          :size="20"
          rounded-full
        />
        <span class="text-xs text-n-slate-10 truncate max-w-[120px]">
          {{ opportunity.contact.name }}
        </span>
      </div>

      <div v-else class="flex-1" />

      <div class="flex items-center gap-1.5 ml-auto">
        <button
          v-if="opportunity.conversation_id"
          class="text-n-slate-9 hover:text-woot-500 transition-colors duration-150 p-0.5 rounded"
          :title="t('CRM.PANEL.OPEN_CONVERSATION')"
          @click="goToConversation"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </button>

        <Avatar
          v-if="opportunity.assigned_agent"
          :src="opportunity.assigned_agent.avatar_url"
          :name="opportunity.assigned_agent.name"
          :size="20"
          rounded-full
          class="flex-shrink-0"
        />
      </div>
    </div>
  </div>
</template>
